import { useState, useEffect, useRef } from "react";
import axios from "axios";

const inputClass =
  "mt-1 block w-full rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 transition-colors";
const labelClass =
  "block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1";

const COMPANY_PROFILE_API = "https://erp-xe9u.onrender.com/api/company-profile";

const RIPPLING_MAP_FIELDS = [
  { key: "hourly", label: "Hourly", hint: "Timesheet pay" },
  { key: "pieceworker", label: "Pieceworker", hint: "Approved piecework logs" },
  { key: "salary", label: "Salary", hint: "Salaried weekly pay" },
  {
    key: "subcontractor",
    label: "Subcontractor",
    hint: "Bills and agreements",
  },
  {
    key: "expense",
    label: "Expense Reimbursement",
    hint: "Material order reimbursements",
  },
  { key: "tax", label: "Tax (HST)", hint: "Tax portion on each line" },
  {
    key: "uniondues",
    label: "Union Dues",
    hint: "Sent as a positive amount",
  },
];
const EMPTY_MAP = { earningTypeId: "", earningCode: "", displayName: "" };

// Draw-once company signature pad (fixed 19:6 canvas to match the PDF box)
function SignaturePadBox({ onSave, saving }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={570}
        height={180}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        className="w-full max-w-md bg-white rounded border border-gray-600"
        style={{ touchAction: "none" }}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={clear}
          className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600"
        >
          Clear
        </button>
        <button
          type="button"
          disabled={!hasInk || saving}
          onClick={() => onSave(canvasRef.current.toDataURL("image/png"))}
          className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
        >
          {saving ? "Saving…" : "Save signature"}
        </button>
      </div>
    </div>
  );
}
// Upload a signature image (PNG/JPG/WebP) → scaled PNG data URL
function SignatureUploadBox({ onSave, saving }) {
  const [srcImg, setSrcImg] = useState(null);
  const [removeBg, setRemoveBg] = useState(true);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|webp)$/.test(file.type))
      return setError("Use a PNG, JPG or WebP image.");
    setError("");
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      setSrcImg(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setError("Could not read that image.");
    };
    img.src = url;
  };

  useEffect(() => {
    if (!srcImg) return;
    const s = Math.min(570 / srcImg.width, 180 / srcImg.height, 1);
    const w = Math.max(1, Math.round(srcImg.width * s));
    const h = Math.max(1, Math.round(srcImg.height * s));
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(srcImg, 0, 0, w, h);
    if (removeBg) {
      const data = ctx.getImageData(0, 0, w, h);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const min = Math.min(px[i], px[i + 1], px[i + 2]);
        if (min >= 235) px[i + 3] = 0;
        else if (min > 200)
          px[i + 3] = Math.round(px[i + 3] * ((235 - min) / 35));
      }
      ctx.putImageData(data, 0, 0);
    }
    setPreview(c.toDataURL("image/png"));
  }, [srcImg, removeBg]);

  const save = () => {
    if (preview.length > 580_000)
      return setError("Image is too detailed. Try a smaller or simpler one.");
    onSave(preview);
  };

  return (
    <div className="space-y-3">
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onFile}
        className="block text-xs text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-gray-200 file:bg-gray-700 file:border file:border-gray-600 file:rounded-md hover:file:bg-gray-600"
      />
      {error && <p className="text-xs text-red-400">{error}</p>}
      {preview && (
        <>
          <div className="inline-block bg-white rounded border border-gray-600 p-2">
            <img src={preview} alt="Preview" className="h-16 w-auto" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-300">
            <input
              type="checkbox"
              checked={removeBg}
              onChange={(e) => setRemoveBg(e.target.checked)}
              className="w-4 h-4"
            />
            Remove white background
          </label>
          <div>
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="px-4 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
            >
              {saving ? "Saving…" : "Save signature"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PluginCard({
  title,
  description,
  connected,
  alias,
  onConnect,
  onDisconnect,
  connectLabel,
  children,
  footer,
}) {
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
      <div className="flex items-start justify-between px-5 py-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            <div className="flex items-center gap-1">
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${connected ? "bg-green-400" : "bg-gray-600"}`}
              />
              <span
                className={`text-xs font-medium ${connected ? "text-green-400" : "text-gray-500"}`}
              >
                {connected ? "Connected" : "Not connected"}
              </span>
            </div>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
          {connected && alias && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-500">Alias:</span>
              <code className="text-xs text-orange-300 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                {alias}
              </code>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4 shrink-0">
          {children}
          {connected ? (
            <button
              onClick={onDisconnect}
              className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-colors border border-red-400/20 hover:border-red-500"
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={onConnect}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
            >
              {connectLabel || "Connect"}
            </button>
          )}
        </div>
      </div>
      {footer}
    </div>
  );
}
export default function PluginsManagement({
  isQuickBooksConnected,
  setIsQuickBooksConnected,
  isAutomatedBillingConnected,
  setIsAutomatedBillingConnected,
  billingAlias,
  setBillingAlias,
  isAutomatedLeadsConnected,
  setIsAutomatedLeadsConnected,
  leadsAlias,
  setLeadsAlias,
  leadSources,
  setLeadSources,
  setDialog,
}) {
  const [newSource, setNewSource] = useState({
    sourceName: "",
    sourceEmail: "",
  });
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [showLeadsConfig, setShowLeadsConfig] = useState(false);
  const [disqualificationEmailBody, setDisqualificationEmailBody] =
    useState("");
  const [outboundEmail, setOutboundEmail] = useState("");
  const [showBillingConfig, setShowBillingConfig] = useState(false);
  const [billingCc, setBillingCc] = useState("");
  const [billingCcSaving, setBillingCcSaving] = useState(false);
  useEffect(() => {
    if (isAutomatedLeadsConnected && showLeadsConfig)
      fetchDisqualificationEmail();
  }, [isAutomatedLeadsConnected, showLeadsConfig]);

  const [subAgStatus, setSubAgStatus] = useState({
    connected: false,
    senderEmail: "",
    senderName: "",
    lastUpdated: null,
    signatureImage: "",
    signerName: "",
    signatureUpdatedAt: null,
  });
  const [editingSig, setEditingSig] = useState(false);
  const [sigMode, setSigMode] = useState("draw"); // "draw" | "upload"
  const [sigSigner, setSigSigner] = useState("");
  const [sigSaving, setSigSaving] = useState(false);
  const [showSubAgConfig, setShowSubAgConfig] = useState(false);
  const [subAgEmail, setSubAgEmail] = useState("");
  const [subAgName, setSubAgName] = useState("");
  const [subAgSaving, setSubAgSaving] = useState(false);

  const [ripplingStatus, setRipplingStatus] = useState({
    connected: false,
    apiKeyLast4: null,
    lastUpdated: null,
  });
  const [showRipplingConfig, setShowRipplingConfig] = useState(false);
  const [ripplingKey, setRipplingKey] = useState("");
  const [ripplingSaving, setRipplingSaving] = useState(false);

  // Rippling earning-type mapping
  const [showRipplingMap, setShowRipplingMap] = useState(false);
  const [earningTypes, setEarningTypes] = useState([]);
  const [typesComplete, setTypesComplete] = useState(true);
  const [earningMappings, setEarningMappings] = useState({});
  const [manualKeys, setManualKeys] = useState({});
  const [earningSearch, setEarningSearch] = useState("");
  const [mapLoading, setMapLoading] = useState(false);
  const [mapSaving, setMapSaving] = useState(false);

  useEffect(() => {
    fetchRipplingStatus();
  }, []);

  const ripplingHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  useEffect(() => {
    fetchSubAgStatus();
  }, []);

  const fetchRipplingStatus = async () => {
    try {
      const res = await axios.get(
        `${COMPANY_PROFILE_API}/rippling/status`,
        ripplingHeaders(),
      );
      setRipplingStatus(res.data);
    } catch (err) {
      console.error("Error fetching Rippling status:", err);
    }
  };

  const handleRipplingSave = async () => {
    if (!ripplingKey.trim()) return;
    setRipplingSaving(true);
    try {
      const res = await axios.put(
        `${COMPANY_PROFILE_API}/rippling/api-key`,
        { apiKey: ripplingKey.trim() },
        ripplingHeaders(),
      );
      setRipplingStatus(res.data);
      setRipplingKey("");
      setShowRipplingConfig(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Rippling connected. API key verified and saved.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message || "Failed to save Rippling API key.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setRipplingSaving(false);
    }
  };

  const handleRipplingDisconnect = async () => {
    try {
      await axios.delete(`${COMPANY_PROFILE_API}/rippling`, ripplingHeaders());
      setRipplingStatus({
        connected: false,
        apiKeyLast4: null,
        lastUpdated: null,
      });
      setRipplingKey("");
      setShowRipplingConfig(false);
      setShowRipplingMap(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Rippling disconnected.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to disconnect Rippling.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const loadMapData = async () => {
    setMapLoading(true);
    try {
      const [typesRes, mapRes] = await Promise.all([
        axios.get(
          `${COMPANY_PROFILE_API}/rippling/earning-types`,
          ripplingHeaders(),
        ),
        axios.get(
          `${COMPANY_PROFILE_API}/rippling/earning-mappings`,
          ripplingHeaders(),
        ),
      ]);
      setEarningTypes(typesRes.data.earningTypes || []);
      setTypesComplete(typesRes.data.complete !== false);
      setEarningMappings(mapRes.data);
    } catch (err) {
      setShowRipplingMap(false);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message:
          err.response?.data?.message ||
          "Failed to load Rippling earning types.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setMapLoading(false);
    }
  };

  const openRipplingMap = async () => {
    if (showRipplingMap) return setShowRipplingMap(false);
    setShowRipplingMap(true);
    setShowRipplingConfig(false);
    await loadMapData();
  };

  const handleMapChange = (key, typeId) => {
    const t = earningTypes.find((x) => x.id === typeId);
    setEarningMappings((m) => ({
      ...m,
      [key]: t
        ? {
            earningTypeId: t.id,
            earningCode: t.earningCode,
            displayName: t.displayName,
          }
        : EMPTY_MAP,
    }));
  };

  const handleManualChange = (key, field, value) => {
    setEarningMappings((m) => ({
      ...m,
      [key]: { ...(m[key] || EMPTY_MAP), [field]: value },
    }));
  };

  const handleMapSave = async () => {
    setMapSaving(true);
    try {
      await axios.put(
        `${COMPANY_PROFILE_API}/rippling/earning-mappings`,
        { mappings: earningMappings },
        ripplingHeaders(),
      );
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Rippling earning type mappings saved.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save mappings.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setMapSaving(false);
    }
  };

  const fetchSubAgStatus = async () => {
    try {
      const res = await axios.get(
        `${COMPANY_PROFILE_API}/sub-agreements/status`,
        ripplingHeaders(),
      );
      setSubAgStatus(res.data);
      setSigSigner(res.data.signerName || "");
      setSubAgEmail(res.data.senderEmail || "");
      setSubAgName(res.data.senderName || "");
    } catch (err) {
      console.error("Error fetching Subcontractor Agreements status:", err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${COMPANY_PROFILE_API}/billing-cc`,
          ripplingHeaders(),
        );
        setBillingCc(res.data.billingCcEmail || "");
      } catch (err) {
        console.error("Error fetching billing CC:", err);
      }
    })();
  }, []);

  const handleBillingCcSave = async () => {
    setBillingCcSaving(true);
    try {
      const res = await axios.put(
        `${COMPANY_PROFILE_API}/billing-cc`,
        { billingCcEmail: billingCc },
        ripplingHeaders(),
      );
      setBillingCc(res.data.billingCcEmail);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: res.data.billingCcEmail
          ? "Billing CC saved. It will be copied on all vendor material order emails."
          : "Billing CC cleared.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save billing CC.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setBillingCcSaving(false);
    }
  };

  const handleSubAgSave = async () => {
    if (!subAgEmail.trim()) return;
    setSubAgSaving(true);
    try {
      const res = await axios.put(
        `${COMPANY_PROFILE_API}/sub-agreements/config`,
        { senderEmail: subAgEmail.trim(), senderName: subAgName.trim() },
        ripplingHeaders(),
      );
      setSubAgStatus(res.data);
      setShowSubAgConfig(false);
      setSubAgStatus((s) => ({ ...s, ...res.data }));
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Subcontractor Agreements email saved.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save settings.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setSubAgSaving(false);
    }
  };

  const handleSubAgDisconnect = async () => {
    try {
      await axios.delete(
        `${COMPANY_PROFILE_API}/sub-agreements`,
        ripplingHeaders(),
      );
      setSubAgStatus({
        connected: false,
        senderEmail: "",
        senderName: "",
        lastUpdated: null,
      });
      setSubAgEmail("");
      setSubAgName("");
      setShowSubAgConfig(false);
      setSubAgStatus((s) => ({
        ...s,
        connected: false,
        senderEmail: "",
        senderName: "",
        lastUpdated: null,
      }));
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Subcontractor Agreements email disconnected.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to disconnect.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleSigSave = async (img) => {
    setSigSaving(true);
    try {
      const res = await axios.put(
        `${COMPANY_PROFILE_API}/sub-agreements/signature`,
        { signature: img, signerName: sigSigner.trim() },
        ripplingHeaders(),
      );
      setSubAgStatus((s) => ({
        ...s,
        signatureImage: res.data.signatureImage,
        signerName: res.data.signerName,
      }));
      setEditingSig(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Company signature saved.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save signature.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } finally {
      setSigSaving(false);
    }
  };

  const handleSigRemove = async () => {
    try {
      await axios.delete(
        `${COMPANY_PROFILE_API}/sub-agreements/signature`,
        ripplingHeaders(),
      );
      setSubAgStatus((s) => ({ ...s, signatureImage: "" }));
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to remove signature.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const fetchDisqualificationEmail = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/automated-leads/disqualification-email",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDisqualificationEmailBody(response.data.disqualificationEmailBody);
      setOutboundEmail(response.data.outboundEmail || "");
    } catch (err) {
      console.error(err);
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to fetch disqualification email template.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleQuickBooksConnect = async () => {
    try {
      const token = localStorage.getItem("token");
      const stateResponse = await axios.get(
        "https://erp-production-72da01c8e651.herokuapp.com/api/auth/quickbooks-state",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { state: hmac, companyId } = stateResponse.data;
      const state = `${companyId}:${hmac}`;
      const clientId = process.env.REACT_APP_QUICKBOOKS_CLIENT_ID;
      const redirectUri =
        "https://erp-production-72da01c8e651.herokuapp.com/api/quickbooks/callback";
      window.location.href = `https://appcenter.intuit.com/connect/oauth2?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=com.intuit.quickbooks.accounting&state=${encodeURIComponent(state)}`;
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to connect to QuickBooks.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleQuickBooksDisconnect = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/plugins/quickbooks/disconnect",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsQuickBooksConnected(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "QuickBooks disconnected successfully!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to disconnect QuickBooks.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleAutomatedBillingConnect = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/automated-billing/create-alias",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsAutomatedBillingConnected(true);
      setBillingAlias(response.data.aliasEmail);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: `Automated Billing connected! Alias: ${response.data.aliasEmail}`,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: true,
        onCopy: () => navigator.clipboard.writeText(response.data.aliasEmail),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to connect Automated Billing.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleAutomatedBillingDisconnect = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/automated-billing/disconnect",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsAutomatedBillingConnected(false);
      setBillingAlias(null);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Automated Billing disconnected.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to disconnect Automated Billing.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleAutomatedLeadsConnect = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/automated-leads/create-alias",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsAutomatedLeadsConnected(true);
      setLeadsAlias(response.data.aliasEmail);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: `Automated Leads connected! Alias: ${response.data.aliasEmail}`,
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: true,
        onCopy: () => navigator.clipboard.writeText(response.data.aliasEmail),
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to connect Automated Leads.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleAutomatedLeadsDisconnect = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/automated-leads/disconnect",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsAutomatedLeadsConnected(false);
      setLeadsAlias(null);
      setLeadSources([]);
      setDisqualificationEmailBody("");
      setOutboundEmail("");
      setShowLeadsConfig(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Automated Leads disconnected.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to disconnect Automated Leads.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleAddLeadSource = async () => {
    if (!newSource.sourceName || !newSource.sourceEmail) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Source name and email are required.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/automated-leads/sources",
        newSource,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLeadSources(response.data);
      setNewSource({ sourceName: "", sourceEmail: "" });
      setIsAddingSource(false);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Lead source added!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to add lead source.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleDeleteLeadSource = async (sourceId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/automated-leads/sources/${sourceId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLeadSources(response.data);
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to delete lead source.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const handleUpdateDisqualificationEmail = async () => {
    if (!disqualificationEmailBody || !outboundEmail) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Outbound email and template are required.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile/automated-leads/disqualification-email",
        { disqualificationEmailBody, outboundEmail },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDisqualificationEmailBody(response.data.disqualificationEmailBody);
      setOutboundEmail(response.data.outboundEmail);
      setDialog({
        open: true,
        type: "success",
        title: "Success",
        message: "Email template saved!",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    } catch (err) {
      setDialog({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to update email template.",
        onConfirm: () => setDialog((p) => ({ ...p, open: false })),
        showCopyButton: false,
      });
    }
  };

  const q = earningSearch.trim().toLowerCase();
  const filteredEarningTypes = earningTypes.filter((t) => {
    if (!q) return true;
    return [t.displayName, t.earningCode, t.id].some((v) =>
      String(v || "")
        .toLowerCase()
        .includes(q),
    );
  });

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-semibold text-orange-400 uppercase tracking-widest">
          Integrations
        </span>
        <div className="flex-1 h-px bg-gray-700" />
      </div>

      {/* QuickBooks */}
      <PluginCard
        title="QuickBooks"
        description="Sync invoicing and accounting directly with your QuickBooks account."
        connected={isQuickBooksConnected}
        onConnect={handleQuickBooksConnect}
        onDisconnect={handleQuickBooksDisconnect}
        connectLabel="Connect QuickBooks"
      >
        {/* QB branded icon button when not connected */}
        {!isQuickBooksConnected && (
          <button
            onClick={handleQuickBooksConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors shadow-sm"
            style={{ backgroundColor: "#2CA01C" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0,0,256,256"
            >
              <g fill="#ffffff" fillRule="nonzero">
                <g transform="scale(8,8)">
                  <path d="M16,3c-7.168,0-13,5.832-13,13c0,7.168,5.832,13,13,13c7.168,0,13-5.832,13-13c0-7.168-5.832-13-13-13zM17,9v12h2h2c2.287,0,4-2.104,4-4.5c0-2.396-1.713-4.5-4-4.5v2c1.027,0,2,1.03,2,2.5c0,1.47-0.973,2.5-2,2.5h-2v-10zM11,11c-2.287,0-4,2.104-4,4.5c0,2.396,1.713,4.5,4,4.5v-2c-1.027,0-2-1.03-2-2.5c0-1.47,0.973-2.5,2-2.5h2v10h2v-12h-1h-1z" />
                </g>
              </g>
            </svg>
            QuickBooks
          </button>
        )}
      </PluginCard>

      {/* Automated Billing */}
      {/* Automated Billing */}
      <PluginCard
        title="Automated Billing"
        description="Generate a billing alias email to automatically process bills from attachments."
        connected={isAutomatedBillingConnected}
        alias={billingAlias}
        onConnect={handleAutomatedBillingConnect}
        onDisconnect={handleAutomatedBillingDisconnect}
        connectLabel="Connect"
        footer={
          showBillingConfig && (
            <div className="border-t border-gray-700 px-5 py-5 space-y-3">
              <div>
                <label className={labelClass}>Billing CC Email</label>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Copied (CC) on every material order email sent to a vendor, so
                  orders land in your billing mailbox. Separate multiple
                  addresses with commas. Leave empty to turn off.
                </p>
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={billingCc}
                  onChange={(e) => setBillingCc(e.target.value)}
                  placeholder="billing@yourcompany.com"
                  className={inputClass}
                />
                <button
                  onClick={handleBillingCcSave}
                  disabled={billingCcSaving}
                  className="mt-1 px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm whitespace-nowrap"
                >
                  {billingCcSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )
        }
      >
        <button
          onClick={() => setShowBillingConfig((v) => !v)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
            showBillingConfig
              ? "bg-gray-600 text-white border-gray-500"
              : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
          }`}
        >
          {showBillingConfig ? "Close Config" : "Configure"}
        </button>
      </PluginCard>

      {/* Automated Leads */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">
                Automated Leads
              </h3>
              <div className="flex items-center gap-1">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${isAutomatedLeadsConnected ? "bg-green-400" : "bg-gray-600"}`}
                />
                <span
                  className={`text-xs font-medium ${isAutomatedLeadsConnected ? "text-green-400" : "text-gray-500"}`}
                >
                  {isAutomatedLeadsConnected ? "Connected" : "Not connected"}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Automatically process inbound leads and map them to sources via
              alias email.
            </p>
            {isAutomatedLeadsConnected && leadsAlias && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-500">Alias:</span>
                <code className="text-xs text-orange-300 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                  {leadsAlias}
                </code>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {isAutomatedLeadsConnected && (
              <button
                onClick={() => setShowLeadsConfig((v) => !v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
                  showLeadsConfig
                    ? "bg-gray-600 text-white border-gray-500"
                    : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
                }`}
              >
                {showLeadsConfig ? "Close Config" : "Configure"}
              </button>
            )}
            {isAutomatedLeadsConnected ? (
              <button
                onClick={handleAutomatedLeadsDisconnect}
                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-colors border border-red-400/20 hover:border-red-500"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={handleAutomatedLeadsConnect}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
              >
                Connect
              </button>
            )}
          </div>
        </div>

        {/* Leads config panel */}
        {isAutomatedLeadsConnected && showLeadsConfig && (
          <div className="border-t border-gray-700 px-5 py-5 space-y-5">
            {/* Email config */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Outbound Email</label>
                <input
                  type="email"
                  value={outboundEmail}
                  onChange={(e) => setOutboundEmail(e.target.value)}
                  placeholder="outbound@company.com"
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-1 flex flex-col">
                <label className={labelClass}>
                  Disqualification Email Template
                </label>
                <textarea
                  value={disqualificationEmailBody}
                  onChange={(e) => setDisqualificationEmailBody(e.target.value)}
                  placeholder="Enter email body sent to disqualified leads…"
                  rows={4}
                  className={`${inputClass} resize-none flex-1`}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={handleUpdateDisqualificationEmail}
                className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
              >
                Save Template
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                Lead Sources
              </span>
              <div className="flex-1 h-px bg-gray-700" />
              <button
                onClick={() => setIsAddingSource((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Source
              </button>
            </div>

            {/* Add source inline form */}
            {isAddingSource && (
              <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Source Name</label>
                    <input
                      type="text"
                      value={newSource.sourceName}
                      onChange={(e) =>
                        setNewSource((p) => ({
                          ...p,
                          sourceName: e.target.value,
                        }))
                      }
                      placeholder="e.g. HomeStars"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Source Email</label>
                    <input
                      type="email"
                      value={newSource.sourceEmail}
                      onChange={(e) =>
                        setNewSource((p) => ({
                          ...p,
                          sourceEmail: e.target.value,
                        }))
                      }
                      placeholder="leads@homestars.com"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => {
                      setIsAddingSource(false);
                      setNewSource({ sourceName: "", sourceEmail: "" });
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddLeadSource}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Sources table */}
            {leadSources.length > 0 ? (
              <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/60">
                    {leadSources.map((source) => (
                      <tr
                        key={source._id}
                        className="hover:bg-gray-700/40 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm text-gray-200">
                          {source.sourceName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {source.sourceEmail}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDeleteLeadSource(source._id)}
                            className="text-xs text-red-400 hover:text-red-300 transition-colors"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-gray-600 text-center py-4">
                No lead sources added yet.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Rippling */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">Rippling</h3>
              <div className="flex items-center gap-1">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${ripplingStatus.connected ? "bg-green-400" : "bg-gray-600"}`}
                />
                <span
                  className={`text-xs font-medium ${ripplingStatus.connected ? "text-green-400" : "text-gray-500"}`}
                >
                  {ripplingStatus.connected ? "Connected" : "Not connected"}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Look up employees and export payroll to Rippling using your API
              key.
            </p>
            {ripplingStatus.connected && ripplingStatus.apiKeyLast4 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-500">API Key:</span>
                <code className="text-xs text-orange-300 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                  ••••••••{ripplingStatus.apiKeyLast4}
                </code>
                {ripplingStatus.lastUpdated && (
                  <span className="text-xs text-gray-600">
                    Updated{" "}
                    {new Date(ripplingStatus.lastUpdated).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {ripplingStatus.connected && (
              <>
                <button
                  onClick={openRipplingMap}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
                    showRipplingMap
                      ? "bg-gray-600 text-white border-gray-500"
                      : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
                  }`}
                >
                  {showRipplingMap ? "Close Config" : "Configure"}
                </button>
                <button
                  onClick={() => {
                    setShowRipplingConfig((v) => !v);
                    setShowRipplingMap(false);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
                    showRipplingConfig
                      ? "bg-gray-600 text-white border-gray-500"
                      : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
                  }`}
                >
                  {showRipplingConfig ? "Close" : "Replace Key"}
                </button>
              </>
            )}
            {ripplingStatus.connected ? (
              <button
                onClick={handleRipplingDisconnect}
                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-colors border border-red-400/20 hover:border-red-500"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={() => setShowRipplingConfig((v) => !v)}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
              >
                {showRipplingConfig ? "Cancel" : "Connect"}
              </button>
            )}
          </div>
        </div>

        {showRipplingConfig && (
          <div className="border-t border-gray-700 px-5 py-5">
            <label className={labelClass}>
              {ripplingStatus.connected ? "New API Key" : "Rippling API Key"}
            </label>
            <div className="flex gap-3">
              <input
                type="password"
                autoComplete="off"
                value={ripplingKey}
                onChange={(e) => setRipplingKey(e.target.value)}
                placeholder="Paste your Rippling API key"
                className={inputClass}
              />
              <button
                onClick={handleRipplingSave}
                disabled={ripplingSaving || !ripplingKey.trim()}
                className="mt-1 px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm whitespace-nowrap"
              >
                {ripplingSaving ? "Verifying…" : "Verify & Save"}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              The key is verified with Rippling, then stored encrypted. It is
              never shown again after saving.
            </p>
          </div>
        )}

        {/* Earning type mapping panel */}
        {ripplingStatus.connected && showRipplingMap && (
          <div className="border-t border-gray-700 px-5 py-5 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label className={labelClass}>Earning Type Mapping</label>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Choose which Rippling earning type each payroll category is
                  posted under. Union dues are sent as a positive amount.
                </p>
              </div>
              <button
                onClick={loadMapData}
                disabled={mapLoading}
                className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-md border border-gray-600 whitespace-nowrap"
              >
                {mapLoading ? "Loading…" : "Refresh from Rippling"}
              </button>
            </div>

            {mapLoading ? (
              <p className="text-xs text-gray-500 py-4">
                Loading earning types from Rippling…
              </p>
            ) : (
              <>
                {!typesComplete && (
                  <div className="text-xs text-orange-300 bg-orange-400/10 border border-orange-400/30 rounded-md px-3 py-2">
                    Only part of the Rippling list could be loaded. If the type
                    you need is missing, use "Enter manually" under that
                    category.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {RIPPLING_MAP_FIELDS.map(({ key, label, hint }) => {
                    const current = earningMappings[key] || EMPTY_MAP;
                    const inList = earningTypes.some(
                      (t) => t.id === current.earningTypeId,
                    );
                    const manual = !!manualKeys[key];
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className={labelClass}>{label}</label>
                          <button
                            type="button"
                            onClick={() =>
                              setManualKeys((p) => ({ ...p, [key]: !p[key] }))
                            }
                            className="text-xs text-orange-400 hover:text-orange-300 mb-1"
                          >
                            {manual ? "Pick from list" : "Enter manually"}
                          </button>
                        </div>

                        {manual ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={current.earningTypeId}
                              onChange={(e) =>
                                handleManualChange(
                                  key,
                                  "earningTypeId",
                                  e.target.value,
                                )
                              }
                              placeholder="Earning type ID"
                              className={inputClass}
                            />
                            <input
                              type="text"
                              value={current.earningCode}
                              onChange={(e) =>
                                handleManualChange(
                                  key,
                                  "earningCode",
                                  e.target.value,
                                )
                              }
                              placeholder="Earning code"
                              className={inputClass}
                            />
                            <input
                              type="text"
                              value={current.displayName}
                              onChange={(e) =>
                                handleManualChange(
                                  key,
                                  "displayName",
                                  e.target.value,
                                )
                              }
                              placeholder="Display name (for your reference)"
                              className={inputClass}
                            />
                          </div>
                        ) : (
                          <select
                            value={current.earningTypeId}
                            onChange={(e) =>
                              handleMapChange(key, e.target.value)
                            }
                            className={inputClass}
                          >
                            <option value="">— Not mapped —</option>
                            {current.earningTypeId && !inList && (
                              <option value={current.earningTypeId}>
                                {current.displayName || current.earningTypeId}{" "}
                                (saved)
                              </option>
                            )}
                            {earningTypes.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.displayName}
                              </option>
                            ))}
                          </select>
                        )}
                        <p className="text-xs text-gray-600">{hint}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleMapSave}
                    disabled={mapSaving}
                    className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm"
                  >
                    {mapSaving ? "Saving…" : "Save Mappings"}
                  </button>
                </div>

                {/* All earning types */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                    All Rippling Earning Types ({earningTypes.length})
                  </span>
                  <div className="flex-1 h-px bg-gray-700" />
                </div>

                <input
                  type="text"
                  value={earningSearch}
                  onChange={(e) => setEarningSearch(e.target.value)}
                  placeholder="Search by name, code or ID…"
                  className={inputClass}
                />

                <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    <table className="min-w-full">
                      <thead className="sticky top-0 bg-gray-800">
                        <tr className="border-b border-gray-700">
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Code / ID
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Mapped to
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            Assign
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700/60">
                        {filteredEarningTypes.length === 0 && (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-3 py-6 text-center text-xs text-gray-600"
                            >
                              No earning types match.
                            </td>
                          </tr>
                        )}
                        {filteredEarningTypes.map((t) => {
                          const mappedTo = RIPPLING_MAP_FIELDS.filter(
                            (f) =>
                              earningMappings[f.key]?.earningTypeId === t.id,
                          );
                          return (
                            <tr key={t.id} className="hover:bg-gray-700/40">
                              <td className="px-3 py-2 text-sm text-gray-200">
                                {t.displayName}
                              </td>
                              <td className="px-3 py-2">
                                <code className="text-xs text-gray-400">
                                  {t.earningCode === t.id
                                    ? t.id
                                    : `${t.earningCode} · ${t.id}`}
                                </code>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {mappedTo.map((f) => (
                                    <span
                                      key={f.key}
                                      className="text-xs text-orange-300 bg-orange-400/10 border border-orange-400/30 rounded px-1.5 py-0.5"
                                    >
                                      {f.label}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <select
                                  value=""
                                  onChange={(e) =>
                                    e.target.value &&
                                    handleMapChange(e.target.value, t.id)
                                  }
                                  className="rounded-md bg-gray-700 border border-gray-600 text-gray-100 text-xs px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                                >
                                  <option value="">Assign to…</option>
                                  {RIPPLING_MAP_FIELDS.map((f) => (
                                    <option key={f.key} value={f.key}>
                                      {f.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  Assigning from this list only changes the mapping on screen.
                  Click Save Mappings to store it.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Subcontractor Agreements */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">
                Subcontractor Agreements
              </h3>
              <div className="flex items-center gap-1">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${subAgStatus.connected ? "bg-green-400" : "bg-gray-600"}`}
                />
                <span
                  className={`text-xs font-medium ${subAgStatus.connected ? "text-green-400" : "text-gray-500"}`}
                >
                  {subAgStatus.connected ? "Connected" : "Not connected"}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Choose the email address agreements are sent from for signature.
              Includes your company logo in the email.
            </p>
            {subAgStatus.connected && subAgStatus.senderEmail && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">Sending as:</span>
                <code className="text-xs text-orange-300 bg-gray-700 px-2 py-0.5 rounded border border-gray-600">
                  {subAgStatus.senderName
                    ? `${subAgStatus.senderName} <${subAgStatus.senderEmail}>`
                    : subAgStatus.senderEmail}
                </code>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {subAgStatus.connected && (
              <button
                onClick={() => setShowSubAgConfig((v) => !v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors border ${
                  showSubAgConfig
                    ? "bg-gray-600 text-white border-gray-500"
                    : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
                }`}
              >
                {showSubAgConfig ? "Close" : "Configure"}
              </button>
            )}
            {subAgStatus.connected ? (
              <button
                onClick={handleSubAgDisconnect}
                className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md transition-colors border border-red-400/20 hover:border-red-500"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={() => setShowSubAgConfig((v) => !v)}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 rounded-md transition-colors shadow-sm"
              >
                {showSubAgConfig ? "Cancel" : "Connect"}
              </button>
            )}
          </div>
        </div>

        {showSubAgConfig && (
          <div className="border-t border-gray-700 px-5 py-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Sender Email</label>
                <input
                  type="email"
                  value={subAgEmail}
                  onChange={(e) => setSubAgEmail(e.target.value)}
                  placeholder="agreements@yourcompany.com"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Sender Name (optional)</label>
                <input
                  type="text"
                  value={subAgName}
                  onChange={(e) => setSubAgName(e.target.value)}
                  placeholder="Defaults to your company name"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-gray-600">
                The email's domain must be verified in your Mailgun account.
                Replies go to this same address.
              </p>
              <button
                onClick={handleSubAgSave}
                disabled={subAgSaving || !subAgEmail.trim()}
                className="px-4 py-2 text-xs font-semibold text-white bg-orange-400 hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors shadow-sm whitespace-nowrap"
              >
                {subAgSaving ? "Saving…" : "Save"}
              </button>
            </div>
            {subAgStatus.connected && (
              <div className="border-t border-gray-700 px-5 py-5 space-y-3">
                <div>
                  <label className={labelClass}>Company Signature</label>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Applied automatically to the Contractor line of every
                    agreement, dated the day it is sent. Draw it once here.
                  </p>
                </div>

                {subAgStatus.signatureImage && !editingSig ? (
                  <div className="space-y-2">
                    <div className="inline-block bg-white rounded border border-gray-600 p-2">
                      <img
                        src={subAgStatus.signatureImage}
                        alt="Company signature"
                        className="h-16 w-auto"
                      />
                    </div>
                    {subAgStatus.signerName && (
                      <p className="text-xs text-gray-400">
                        Signed by {subAgStatus.signerName}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingSig(true)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-md border border-gray-600"
                      >
                        Replace
                      </button>
                      <button
                        onClick={handleSigRemove}
                        className="px-3 py-1.5 text-xs font-medium text-red-400 hover:text-white bg-red-400/10 hover:bg-red-500 rounded-md border border-red-400/20 hover:border-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="max-w-md">
                      <label className={labelClass}>
                        Signer name (printed under signature)
                      </label>
                      <input
                        type="text"
                        value={sigSigner}
                        onChange={(e) => setSigSigner(e.target.value)}
                        placeholder="e.g. Jane Smith, President"
                        className={inputClass}
                      />
                    </div>

                    <div className="flex gap-1">
                      {[
                        ["draw", "Draw"],
                        ["upload", "Upload image"],
                      ].map(([k, label]) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setSigMode(k)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                            sigMode === k
                              ? "bg-gray-600 text-white border-gray-500"
                              : "text-gray-300 bg-gray-700 hover:bg-gray-600 border-gray-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {sigMode === "draw" ? (
                      <SignaturePadBox
                        onSave={handleSigSave}
                        saving={sigSaving}
                      />
                    ) : (
                      <SignatureUploadBox
                        onSave={handleSigSave}
                        saving={sigSaving}
                      />
                    )}

                    {subAgStatus.signatureImage && (
                      <button
                        onClick={() => setEditingSig(false)}
                        className="text-xs text-gray-400 hover:text-gray-200"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
